import _ from "lodash";
import React from "react";

import { IImportSetting } from "src/core/shared/api/importSetting.api";
import { IFormField } from "src/library/components/Forms";
import { validateHeaderMappingField } from "src/library/utils/validation";
import { IAddress } from "src/library/components/Address";

import { IPendingCustomer } from "../api/pendingCustomer.api";

/**
 * @description Overrides IAddress to provide a camelcase typed postCode key.
 * The motivation being that the pending customer import header for postcode is keyed as 'PostCode'
 * and we cannot match this against existing postcode data stored keyed as postcode without custom logic.
 */
interface ExistingAddressFormData extends Omit<IAddress, "postcode"> {
  postCode?: string | null;
}

/**
 * @description Extends IPendingCustomer overriding address definition to provide a camelcase key to postcode.
 */
type ExistingPendingCustomerData = Omit<IPendingCustomer, "address"> & { address?: ExistingAddressFormData };

const useGenerateCustomerFormFields = () => {
  /**
   * @description Transforms customer import settings into a collection of IFormFields for consumption by a Form component.
   *
   * @param {IImportSetting} newCustomerImportSetting - Import settings to create fields for.
   * @param {ExistingPendingCustomerData} [existingData] - If provided, seeds initial values for the returned fields.
   * @return {IFormField[]} For consumption by a Form
   */
  const generateFormFields = React.useCallback((newCustomerImportSetting: IImportSetting, existingData?: ExistingPendingCustomerData) => {
    const registerFormFields: IFormField[] = [];
    const groupings = newCustomerImportSetting.options?.formFieldGroups;

    // Generate the field sets based on the formFieldGroups
    const fieldSets: Record<string, IFormField[]> = {
      general: [],
      address: [],
      contact: [],
      furtherInformation: []
    };

    for (const headerItem of newCustomerImportSetting.headerMappings) {
      // If existing value is present in the provided existing data, find it and use as the default value.
      const existingValue =
      existingData?.[ _.camelCase(headerItem.internal) ] ??
      existingData?.customData?.[ headerItem.internal ] ??
      existingData?.address?.[ _.camelCase(headerItem.internal) ];

      let formFieldConfig: IFormField = {
        type: headerItem.formFieldType || "input",
        name: headerItem.internal,
        inputType: headerItem.type || "text",
        label: _.startCase(headerItem.external),
        value: existingValue
      };

      if (headerItem.validation) {
        formFieldConfig.validation = validateHeaderMappingField(headerItem);
      }

      if (headerItem.options) {
        formFieldConfig = {
          ...formFieldConfig,
          options: headerItem.options.map(o => ({
            value: o,
            text: _.startCase(o)
          })),
          unselectedOption: {
            text: "Please select an option",
            value: ""
          }
        };
      }

      if (groupings?.address.includes(headerItem.internal)) {
        fieldSets.address.push({
          ...formFieldConfig,
          placeholder: headerItem.external
        });
      } else if (groupings?.contact.includes(headerItem.internal)) {
        fieldSets.contact.push(formFieldConfig);
      } else if (groupings?.furtherInformation.includes(headerItem.internal)) {
        fieldSets.furtherInformation.push(formFieldConfig);
      } else {
        // Default group to General
        fieldSets.general.push(formFieldConfig);
      }
    }

    // Generate the fieldSet shape IForm requires
    for (const fieldSet in fieldSets) {
      const fieldSetFields = fieldSets[ fieldSet ];

      if (fieldSet === "address") {
        registerFormFields.push({
          type: "fieldset",
          name: fieldSet,
          label: fieldSet,
          fields: [ ...fieldSetFields ]
        });
      } else {
        const groupTitle: IFormField = {
          type: "title",
          name: _.startCase(fieldSet),
          label: _.startCase(fieldSet)
        };

        registerFormFields.push(...[ groupTitle, ...fieldSetFields ]);
      }
    }

    return registerFormFields;
  }, []);

  return { generateFormFields };
};

export default useGenerateCustomerFormFields;